import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AnalyticSearchRequestDto, ChartResponseDto, InvoiceSortDto } from 'src/generated/services/TFinancialApi';
import { InvoiceColumnHeader } from './InvoiceColumnHeader';
import { TableDetail } from './TableDetail';
import { useTableFilterState } from './hooks/useTableFilterState';
import { buildInvoiceFilterSearchRequest, mergeTableFiltersIntoRequest } from './analyticsSearchRequestBuilders';
import { INVOICE_SORTABLE_COLUMNS } from './constants/invoiceColumnConfig';
import { getInvoicesColumns, TableTab } from './useTable';
import { InvoiceTableFilters, UnifiedAnalyticsFilters } from './analyticsTableFilterTypes';
import { useGetInvoicesData } from './useTableData';
import { useAnalyticsData } from '../context/AnalyticsDataContext';
import { useColumnVisibility } from '../../../molecules/table/columnSettings';

// Mapping for invoice field names to field IDs
const invoiceFieldNameMap: Record<string, string> = {
  accountNumber: 'ACCOUNT_NUMBER',
  invoiceNumber: 'INVOICE_NUMBER',
  date: 'DATE_REF',
  shipmentNumber: 'SHIPMENT_COUNT',
  invoiceChargesCount: 'INVOICE_CHARGES_COUNT',
  billedEntity: 'BILLED_ENTITY_NAME',
  totalAmount: 'INVOICE_TOTAL_AMOUNT',
  totalAmountInReferenceCurrency: 'TOTAL_AMOUNT',
  fapRef: 'FAP_REF',
  resolutionDate: 'RESOLUTION_DATE',
  reason: 'INVOICE_REASON',
  invoiceStatus: 'INVOICE_STATUS',
  serviceProvider: 'SERVICE_PROVIDER_NAME',
  modality: 'MODALITY',
  shipper: 'SHIPPER_NAME',
  pol: 'POL',
  shipperCountry: 'SHIPPER_COUNTRY',
  consignee: 'CONSIGNEE_NAME',
  pod: 'POD',
  consigneeCountry: 'CONSIGNEE_COUNTRY',
};

const EMPTY_ARRAY: string[] = [];

const invoiceFilterToSelectionMap = (filters: InvoiceTableFilters) => ({
  invoiceNumber: filters.invoiceNumber ?? [],
  fapRef: filters.fapRef ?? [],
  accountNumber: filters.accountNumber ?? [],
  invoiceStatus: filters.invoiceStatus ?? [],
  reason: filters.invoiceReason ?? [],
  serviceProvider: filters.serviceProviderCode ?? [],
  modality: filters.modality ?? [],
  billedEntity: filters.billedEntity ?? [],
  shipmentNumber: filters.shipmentNumber ?? [],
  invoiceChargesCount: filters.invoiceChargesCount ?? [],
});

const invoiceColumnIdToFilterKey = (columnId: string) => {
  if (columnId === 'serviceProvider') return 'serviceProviderCode';
  if (columnId === 'reason') return 'invoiceReason';
  if (columnId === 'status') return 'invoiceStatus';

  return columnId;
};

interface InvoiceTableProps {
  chartData: ChartResponseDto;
  page: { pageNumber: number; pageSize: number };
  initialSearchRequest: AnalyticSearchRequestDto;
  isTabVisited: boolean;
  dateReference: string;
  currentCurrency: string;
  sharedFilters?: UnifiedAnalyticsFilters;
  onFilterChange?: (columnId: string, selectedValues: string[]) => void;
}

export const InvoiceTable: React.FC<InvoiceTableProps> = ({
  chartData,
  page,
  initialSearchRequest,
  isTabVisited,
  dateReference,
  currentCurrency,
  sharedFilters,
  onFilterChange,
}) => {
  // Access context for updating totals and drawer filters
  const { updateTotal, drawerFilters } = useAnalyticsData();

  // Sorting state
  const [sortingField, setSortingField] = useState<InvoiceSortDto['field']>('INVOICE_NUMBER');
  const [sortingMethod, setSortingMethod] = useState<string>('ASC');

  // Filter state management
  const { tableFilters, filterSelections, handleColumnFilter } = useTableFilterState<InvoiceTableFilters>({
    initialFilters: {
      // Cross-filter fields (shared across all tables)
      shipmentNumber: [],
      invoiceNumber: [],
      serviceProviderCode: [],
      modality: [],
      billedEntity: [],
      // Invoice-specific fields
      invoiceStatus: [],
      fapRef: [],
      accountNumber: [],
      invoiceReason: [],
      chargeCount: [],
      invoiceChargesCount: [],
    },
    externalFilters: sharedFilters as unknown as InvoiceTableFilters,
    onExternalFilterChange: onFilterChange,
    filterToSelectionMap: invoiceFilterToSelectionMap,
    columnIdToFilterKey: invoiceColumnIdToFilterKey,
  });

  const searchRequest = useMemo(
    () => mergeTableFiltersIntoRequest(initialSearchRequest, tableFilters as unknown as UnifiedAnalyticsFilters, 'invoice'),
    [initialSearchRequest, tableFilters]
  );

  // Data fetching
  const {
    data: invoicesData,
    refetch: refetchData,
    isLoading,
  } = useGetInvoicesData(
    {
      page,
      searchRequest,
      sorting: {
        field: sortingField,
        direction: sortingMethod as InvoiceSortDto['direction'],
      },
    },
    isTabVisited
  );

  // Column configuration
  const columns = useMemo(() => getInvoicesColumns({ dateReference, currentCurrency }), [dateReference, currentCurrency]);

  // Column visibility management
  const { columns: columnConfigs } = useColumnVisibility();

  // Sorting handler
  const handleSort = useCallback(
    (method: string, fieldName: string) => {
      setSortingMethod(method);
      const fieldId = fieldName === 'date' ? dateReference : invoiceFieldNameMap[fieldName] || 'INVOICE_NUMBER';
      setSortingField(fieldId as InvoiceSortDto['field']);
    },
    [dateReference]
  );

  // Enhanced columns with Invoice headers
  const enhancedColumns = useMemo(() => {
    // Create a map of default columns for easy lookup
    const defaultColumnsMap = new Map(columns.map((col) => [col.accessorKey as string, col]));

    // Use columnConfigs to determine order and visibility
    return columnConfigs
      .filter((config) => config.isVisible && defaultColumnsMap.has(config.id))
      .map((config) => {
        const col = defaultColumnsMap.get(config.id);
        const accessorKey = col.accessorKey as string;

        if (!accessorKey || !INVOICE_SORTABLE_COLUMNS.has(accessorKey)) {
          return col;
        }

        const customHeader = col.Header || col.header;

        // Map accessorKey to drawer filter key
        const getDrawerFilterKey = (key: string): keyof typeof drawerFilters | null => {
          switch (key) {
            case 'serviceProvider':
              return 'serviceProviderCode';
            case 'reason':
              return 'invoiceReason';
            case 'status':
              return 'invoiceStatus';
            default:
              return key as keyof typeof drawerFilters;
          }
        };

        const drawerFilterKey = getDrawerFilterKey(accessorKey);
        const drawerFilterValue = drawerFilterKey ? drawerFilters[drawerFilterKey] ?? [] : [];

        return {
          ...col,
          Header: ({ column }) => (
            <InvoiceColumnHeader
              column={column}
              accessorKey={accessorKey}
              filterSearchRequest={buildInvoiceFilterSearchRequest(accessorKey, searchRequest, tableFilters)}
              initialSelectedValues={filterSelections[accessorKey] ?? []}
              drawerFilterValue={drawerFilterValue}
              onFilter={handleColumnFilter}
              onSort={handleSort}
              customHeader={customHeader}
            />
          ),
        };
      });
  }, [columns, columnConfigs, searchRequest, tableFilters, filterSelections, handleColumnFilter, handleSort, drawerFilters]);

  const tableDetail = {
    columns: enhancedColumns,
    data: invoicesData?.invoices,
    total: invoicesData?.total,
    page: invoicesData?.page,
    isLoading: isTabVisited && (isLoading || !invoicesData),
    isTabVisited,
  };

  return (
    <TableDetail tableDetail={tableDetail} chartData={chartData} page={page} tableTab={TableTab.INVOICES} refetchData={refetchData} onSorting={handleSort} />
  );
};
